import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { GetToken, scorecardApi } from '../utils/scorecardApi';
import '../css/scorecard.css';

interface ScorecardPhotoProps {
  uploadId: number;
  getToken: GetToken;
  /** Box height; leave it out to let the page's CSS size the box (.sc-photo). */
  height?: number | string;
  /** Change to force a reload (e.g. after a re-read with a new photo). */
  version?: string | number;
  /** Shown under the photo so the reviewer can tell it's the photo they took. */
  caption?: React.ReactNode;
}

interface View { scale: number; rotate: number; x: number; y: number }

/**
 * The uploaded scorecard photo, fetched with the user's token (it isn't
 * public), with pinch / wheel zoom, drag to pan, 90° rotation either way —
 * card photos are usually taken sideways — and a link to the full-size photo.
 */
const ScorecardPhoto: React.FC<ScorecardPhotoProps> = ({ uploadId, getToken, height, version, caption }) => {
  const boxRef = useRef<HTMLDivElement>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const [box, setBox] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const [view, setView] = useState<View>({ scale: 1, rotate: 0, x: 0, y: 0 });
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  // Callers often pass a fresh token getter each render; only the photo matters.
  const tokenRef = useRef(getToken);
  tokenRef.current = getToken;
  const pinch = useRef<{ dist: number } | null>(null);

  useEffect(() => {
    let alive = true;
    let objectUrl: string | null = null;
    setUrl(null);
    setError(null);
    scorecardApi.photoUrl(() => tokenRef.current(), uploadId)
      .then((u) => { objectUrl = u; if (alive) setUrl(u); else URL.revokeObjectURL(u); })
      .catch((e) => { if (alive) setError(e.message || 'Photo not available.'); });
    return () => { alive = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [uploadId, version]);

  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    const measure = () => setBox({ w: el.clientWidth, h: el.clientHeight });
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const fitScale = useCallback((rotate: number) => {
    if (!natural || !box.w || !box.h) return 1;
    const sideways = Math.abs(rotate % 180) === 90;
    const w = sideways ? natural.h : natural.w;
    const h = sideways ? natural.w : natural.h;
    return Math.min(box.w / w, box.h / h);
  }, [natural, box]);

  const fit = useCallback((rotate: number) => setView({ scale: fitScale(rotate), rotate, x: 0, y: 0 }), [fitScale]);

  // Scorecards are wide: a portrait photo is a card shot sideways, so start it
  // turned to read across (whatever the box's shape — ↺ / ↻ fix the direction).
  useEffect(() => {
    if (!natural || !box.w) return;
    const startRotate = natural.h > natural.w * 1.15 ? -90 : 0;
    fit(startRotate);
  }, [natural, box.w, box.h]); // eslint-disable-line react-hooks/exhaustive-deps

  const zoomBy = (k: number) => setView((v) => {
    const min = fitScale(v.rotate) * 0.8;
    return { ...v, scale: Math.max(min, Math.min(v.scale * k, fitScale(v.rotate) * 8)) };
  });

  const onPointerDown = (e: React.PointerEvent) => {
    (e.target as Element).setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = Array.from(pointers.current.values());
      pinch.current = { dist: Math.hypot(a.x - b.x, a.y - b.y) };
    }
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const prev = pointers.current.get(e.pointerId);
    if (!prev) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2 && pinch.current) {
      const [a, b] = Array.from(pointers.current.values());
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      if (pinch.current.dist > 0) zoomBy(dist / pinch.current.dist);
      pinch.current.dist = dist;
    } else if (pointers.current.size === 1) {
      const dx = e.clientX - prev.x;
      const dy = e.clientY - prev.y;
      setView((v) => ({ ...v, x: v.x + dx, y: v.y + dy }));
    }
  };
  const onPointerUp = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
  };

  return (
    <figure className="sc-photo-figure">
      <div
        ref={boxRef}
        className="sc-photo"
        style={height != null ? { height } : undefined}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onWheel={(e) => zoomBy(e.deltaY < 0 ? 1.12 : 1 / 1.12)}
        onDoubleClick={() => (view.scale > fitScale(view.rotate) * 1.05 ? fit(view.rotate) : zoomBy(2.2))}
      >
        {url && (
          <img
            src={url}
            alt="Uploaded scorecard"
            onLoad={(e) => setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
            style={{
              width: natural?.w,
              height: natural?.h,
              transform: `translate(-50%, -50%) translate(${view.x}px, ${view.y}px) rotate(${view.rotate}deg) scale(${view.scale})`,
            }}
            draggable={false}
          />
        )}
        {!url && !error && <div className="sc-photo-msg">Loading photo…</div>}
        {error && <div className="sc-photo-msg">{error}</div>}
        {url && (
          <div className="sc-photo-tools" onPointerDown={(e) => e.stopPropagation()}>
            <button type="button" aria-label="Zoom out" onClick={() => zoomBy(1 / 1.4)}>−</button>
            <button type="button" aria-label="Zoom in" onClick={() => zoomBy(1.4)}>+</button>
            <button type="button" aria-label="Rotate left" onClick={() => fit(view.rotate - 90)}>↺</button>
            <button type="button" aria-label="Rotate right" onClick={() => fit(view.rotate + 90)}>↻</button>
            <button type="button" aria-label="Fit" onClick={() => fit(view.rotate)}>⤢</button>
            <a className="sc-photo-open" href={url} target="_blank" rel="noopener noreferrer" aria-label="Open full size" title="Open full size">⇱</a>
          </div>
        )}
      </div>
      {caption && <figcaption className="sc-photo-caption">{caption}</figcaption>}
    </figure>
  );
};

export default ScorecardPhoto;
